import React, { useState, useCallback } from 'react';
import {
    Box, Typography, Button, CircularProgress, Alert, List, ListItem, ListItemText,
    Divider, Paper, Chip
} from '@mui/material';
import { Search, AddCircleOutline, Cancel } from '@mui/icons-material';


// Use the same definitions as AdminModal
const SUPPORTED_BOOKING_SYSTEMS = [
    { key: 'mock', label: 'Mock (Testing)' },
    { key: 'foreup', label: 'ForeUp' },
    { key: 'chronogolf', label: 'ChronoGolf' },
    { key: 'teeitup', label: 'TeeItUp' },
    { key: 'teeon', label: 'TeeOn' },
    { key: 'clubprophet', label: 'Club Prophet' },
];


const discoveryToolPaperStyle = {
    p: 2,
    mt: 2, // Margin Top to separate from other elements
    mb: 2,
    border: '1px dashed',
    borderColor: 'info.main' // Use a different color to distinguish
};

// Add authenticatedAxios to the props
function CourseDiscoveryTool({ club, existingCourses, onAddCourses, onCancel, authenticatedAxios }) {
    const [results, setResults] = useState(null); // null: initial, []: no new found, [...]: found courses
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Get system and identifier from the club prop for display only
    const systemType = club?.booking_system_type;
    const systemLabel = SUPPORTED_BOOKING_SYSTEMS.find(s => s.key === systemType)?.label || systemType || 'N/A';
    const apiIdentifier = club?.api_identifier || 'N/A';
    const clubId = club?.id; // <<<< Ensure club object has 'id' property >>>>

    const handleDiscover = useCallback(async () => {
        // Check if club ID is present
        if (!clubId) {
             setError('Club ID is missing, cannot perform discovery.');
             console.error("Discovery called without club ID", club);
             return;
        }
        // Also check if system/id are set, as backend requires them (though backend checks too)
         if (!systemType || !club.api_identifier) { // Check original club.api_identifier
             setError('Club Booking System Type and API Identifier must be set on the club before discovery.');
             return;
         }


        setLoading(true);
        setError('');
        setResults(null); // Reset results
        let discovered = [];

        console.log(`Requesting discovery via backend for Club ID: ${clubId}`);

        try {
            // --- Call the Backend Discovery Endpoint using authenticatedAxios ---
            console.log("Calling Backend Discovery for club ID:", clubId);

            const response = await authenticatedAxios.get(`/api/discover/courses/${clubId}`);
            console.log("Backend Discovery Response:", response.data);

            // Backend now returns the processed list directly
            // Ensure the backend returns { courses: [...] }
             discovered = response.data.courses || [];


            // --- Filter out duplicates based on API IDENTIFIER (Frontend logic remains) ---
            const existingApiIdentifiers = new Set(
                existingCourses.map(c => c.api_identifier) // Simple check based on API ID
            );

            const newUniqueCourses = discovered.filter(
                disc => !existingApiIdentifiers.has(disc.api_identifier)
            );

            console.log("Existing API Identifiers:", Array.from(existingApiIdentifiers));
            console.log("Discovered Raw (from Backend):", discovered);
            console.log("New Unique Courses (filtered):", newUniqueCourses);

            setResults(newUniqueCourses); // Store only new, unique courses

            if (newUniqueCourses.length === 0 && discovered.length > 0) {
                setError(`All ${discovered.length} discovered course(s) seem to already exist for this club (based on API Identifier).`);
            } else if (newUniqueCourses.length === 0 && discovered.length === 0) {
                 // Check if the backend might have sent an empty list intentionally vs an error earlier
                 // If backend endpoint succeeded but returned empty, this is the correct message.
                 setError(`No courses found via backend discovery for '${systemLabel}' using identifier '${apiIdentifier}'. The club/courses might not exist on the platform or the identifier is incorrect.`);
            }


        } catch (err) {
            // Check for authentication errors first
            if (err.response && err.response.status === 401) {
                alert('Your authentication session has expired. Please log in again.');
                onCancel(); // Close discovery tool
                return;
            }
            
            let errorMsg = 'Discovery failed: ';
            if (err.response) {
                const detail = err.response?.data?.detail;
                const status = err.response?.status;
                errorMsg += `${status ? `(Status ${status}) ` : ''}${detail || err.message || 'Unknown error'}`;
            } else {
                errorMsg += err.message || 'Unknown error';
            }
            setError(errorMsg);
            console.error("Discovery Error:", err.response?.data || err.message || err);
            setResults(null); // Clear results on error
        } finally {
            setLoading(false);
        }
    }, [clubId, systemType, existingCourses, systemLabel, club, authenticatedAxios, apiIdentifier, onCancel]);

    const handleAddClick = () => {
        if (results && results.length > 0) {
            // Pass the results up to the parent to handle the actual saving
            // The results objects should already have the correct structure from the backend
            onAddCourses(results);
            // Optionally reset state here or let parent handle closing the tool
        }
    };

    const handleCancelClick = () => {
        onCancel(); // Parent will handle closing
    };


    return (
        <Paper sx={discoveryToolPaperStyle}>
            <Typography variant="h6" gutterBottom>Auto-Discover Courses</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body1">Using club settings:</Typography>
                 {/* Display System and Identifier for user context */}
                 <Chip label={`System: ${systemLabel}`} size="small" />
                <Chip label={`Identifier: ${apiIdentifier}`} size="small" />
                <Button
                    startIcon={<Search />}
                    onClick={handleDiscover}
                    variant="contained"
                    color="info"
                    // Disable if loading OR if essential info is missing
                    disabled={loading || !clubId || !systemType || !club?.api_identifier}
                    size="small"
                    sx={{ ml: 'auto' }} // Push button to the right
                    title={!clubId || !systemType || !club?.api_identifier ? "Club must have ID, System Type, and API Identifier set" : "Discover courses via backend"}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Discover Courses'}
                </Button>
            </Box>

            {error && <Alert severity={error.includes("already exist") ? "warning" : "error"} sx={{ mt: 2 }}>{error}</Alert>}

            {results !== null && ( // Only show results section if discovery has been attempted
                 <Box mt={2}>
                     <Divider sx={{ mb: 1 }}/>
                     {results.length > 0 ? (
                         <>
                             <Typography variant="subtitle1" gutterBottom>Found {results.length} new course(s):</Typography>
                             <List dense sx={{ maxHeight: 150, overflowY: 'auto', mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                 {results.map((course, index) => (
                                     // Use a more robust key if possible, index is fallback
                                     <ListItem key={`${clubId}-${course.api_identifier}-${index}`} dense>
                                         <ListItemText
                                             primary={`${course.name} (${course.holes} holes)`}
                                             secondary={`API ID: ${course.api_identifier}`} // System type is implied by the club context
                                             primaryTypographyProps={{ variant: 'body2' }}
                                             secondaryTypographyProps={{ variant: 'caption' }}
                                         />
                                     </ListItem>
                                 ))}
                             </List>
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Button
                                      startIcon={<AddCircleOutline />}
                                      onClick={handleAddClick}
                                      variant="contained"
                                      color="success"
                                      disabled={loading} // Consider disabling if parent is saving
                                      size="small"
                                  >
                                      Add These Courses ({results.length})
                                  </Button>
                                   <Button
                                       startIcon={<Cancel />}
                                       onClick={handleCancelClick}
                                       variant="outlined"
                                       size="small"
                                       disabled={loading}
                                   >
                                       Cancel
                                   </Button>
                               </Box>
                         </>
                     ) : (
                         // Show 'no new courses' only if search was attempted, successful, but yielded no *new* results
                          !error && <Typography variant="body2" color="textSecondary">No *new* courses found for this club.</Typography>
                     )}
                      {/* Show Cancel/Close button if results are empty but search was attempted without error */}
                      {results.length === 0 && !error && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button
                                  startIcon={<Cancel />}
                                  onClick={handleCancelClick}
                                  variant="outlined"
                                  size="small"
                                  disabled={loading}
                              >
                                  Close Discovery
                              </Button>
                           </Box>
                      )}
                 </Box>
            )}
             {/* Show initial cancel button if tool is open but search hasn't run */}
              {results === null && !loading && !error && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                       <Button
                           startIcon={<Cancel />}
                           onClick={handleCancelClick}
                           variant="outlined"
                           size="small"
                       >
                           Cancel Discovery
                       </Button>
                   </Box>
              )}
        </Paper>
    );
}

export default CourseDiscoveryTool;